import { create } from "zustand";
import { storage } from "../lib/storage";

/**
 * Trade-mode account (spec §7, §11 `accountStore`). A running simulated cash
 * balance that each trade's dollar P&L moves, plus trade stats. Each trade
 * sizes to the player's own chosen risk (lib/trade), so the balance is the
 * whole scoreboard. For-fun only, never real money. Persisted; resettable.
 */

export const DEFAULT_BALANCE = 10000;

export type AccountStats = {
  trades: number;
  wins: number;
  losses: number;
  /** Sum of winning P&L (≥ 0) and losing P&L (≤ 0), for the averages. */
  sumWin: number;
  sumLoss: number;
  biggestWin: number;
  biggestLoss: number;
};

const ZERO_STATS: AccountStats = {
  trades: 0,
  wins: 0,
  losses: 0,
  sumWin: 0,
  sumLoss: 0,
  biggestWin: 0,
  biggestLoss: 0,
};

// Fresh keys, not a migration: balances banked under the old unit-sized model
// are not comparable with fixed-notional ones. Existing players start over.
const BALANCE_KEY = "account:balance2";
const STATS_KEY = "account:stats2";

export interface AccountState {
  balance: number;
  stats: AccountStats;
  /** Realize a trade's dollar P&L into the balance and stats. */
  applyTrade: (pnl: number) => void;
  reset: () => void;
}

export const useAccountStore = create<AccountState>((set, get) => ({
  balance: storage.get<number>(BALANCE_KEY, DEFAULT_BALANCE),
  stats: storage.get<AccountStats>(STATS_KEY, ZERO_STATS),

  applyTrade: (pnl) => {
    const { balance, stats } = get();
    const next: AccountStats = {
      trades: stats.trades + 1,
      wins: stats.wins + (pnl > 0 ? 1 : 0),
      losses: stats.losses + (pnl < 0 ? 1 : 0),
      sumWin: stats.sumWin + (pnl > 0 ? pnl : 0),
      sumLoss: stats.sumLoss + (pnl < 0 ? pnl : 0),
      biggestWin: pnl > 0 ? Math.max(stats.biggestWin, pnl) : stats.biggestWin,
      biggestLoss: pnl < 0 ? Math.min(stats.biggestLoss, pnl) : stats.biggestLoss,
    };
    const nextBalance = balance + pnl;
    storage.set(BALANCE_KEY, nextBalance);
    storage.set(STATS_KEY, next);
    set({ balance: nextBalance, stats: next });
  },

  reset: () => {
    storage.set(BALANCE_KEY, DEFAULT_BALANCE);
    storage.set(STATS_KEY, ZERO_STATS);
    set({ balance: DEFAULT_BALANCE, stats: { ...ZERO_STATS } });
  },
}));

/** Win rate as a fraction 0..1 (0 when no trades). */
export function winRate(stats: AccountStats): number {
  return stats.trades === 0 ? 0 : stats.wins / stats.trades;
}

/** Average winning P&L (0 when no wins). */
export function avgWin(stats: AccountStats): number {
  return stats.wins === 0 ? 0 : stats.sumWin / stats.wins;
}

/** Average losing P&L (≤ 0; 0 when no losses). */
export function avgLoss(stats: AccountStats): number {
  return stats.losses === 0 ? 0 : stats.sumLoss / stats.losses;
}
